import { DataRoomAccessStatus, DataRoomStatus } from './data-room.enums';
import { DataRoomActivityEventType, DataRoomDownloadScope } from './data-room-activity.enums';

/**
 * The vocabulary every Activity tab shares — 20.1.0.
 *
 * That story is explicit that 20.1.1–20.1.4 "must not introduce competing calculation logic", so
 * what a window is, what a metric is called and how a duration reads live here and are imported,
 * never restated. The arithmetic itself is in the read layer, in one set of named SQL fragments,
 * for the same reason.
 */
export enum DataRoomActivityWindow {
  DAYS_7 = '7-days',
  DAYS_30 = '30-days',
  DAYS_90 = '90-days',
}

export const DEFAULT_DATA_ROOM_ACTIVITY_WINDOW = DataRoomActivityWindow.DAYS_7;

export const DATA_ROOM_ACTIVITY_WINDOW_DAYS: Record<DataRoomActivityWindow, number> = {
  [DataRoomActivityWindow.DAYS_7]: 7,
  [DataRoomActivityWindow.DAYS_30]: 30,
  [DataRoomActivityWindow.DAYS_90]: 90,
};

export interface DataRoomActivityRange {
  from: string;
  to: string;
}

const startOfUtcDay = (at: Date): Date =>
  new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * 20.1.0 §11: the window is always relative to today — "7 Days" is the seven days ending today,
 * not a calendar week. Whole days, so the engagement chart has exactly that many buckets and the
 * first of them is a full day rather than a stub.
 *
 * Days are cut in UTC. The alternative is the viewer's own midnight, which the server is never
 * told; picking one timezone and naming it beats two screens disagreeing about which day a
 * Tuesday-evening view fell on.
 */
export const dataRoomActivityRange = (
  window: DataRoomActivityWindow,
  now = new Date(),
): DataRoomActivityRange => ({
  from: new Date(
    startOfUtcDay(now).getTime() - (DATA_ROOM_ACTIVITY_WINDOW_DAYS[window] - 1) * DAY_MS,
  ).toISOString(),
  to: now.toISOString(),
});

/**
 * The window of equal length immediately before this one, for the trend arrow 20.1.3 §3 asks for.
 * Here rather than on that tab so "the preceding period" means one thing.
 */
export const dataRoomActivityPreviousRange = (
  window: DataRoomActivityWindow,
  now = new Date(),
): DataRoomActivityRange => {
  const current = dataRoomActivityRange(window, now);
  const span = DATA_ROOM_ACTIVITY_WINDOW_DAYS[window] * DAY_MS;

  return {
    from: new Date(new Date(current.from).getTime() - span).toISOString(),
    to: current.from,
  };
};

/**
 * The week a weekly digest reports on — 20.4 §4: the previous calendar week, Monday to Sunday.
 *
 * A calendar week and not a rolling seven days, which is the one place the Data Room departs from
 * `dataRoomActivityRange`: a digest that arrives on Monday is about "last week", and a rolling
 * window would cover half of the week the reader is already in.
 *
 * Cut in UTC, like every other day boundary in this file. 20.4 asks for the company's local
 * timezone; there is no column that says what that is, and inventing one per company inside a
 * date helper would be a different answer than the rest of the Activity tab gives.
 */
export const dataRoomWeeklyDigestRange = (now = new Date()): DataRoomActivityRange => {
  const midnight = startOfUtcDay(now);
  // getUTCDay() is 0 on Sunday, so Monday is 1 and the days back to this week's Monday is 6 then.
  const daysSinceMonday = (midnight.getUTCDay() + 6) % 7;
  const thisMonday = midnight.getTime() - daysSinceMonday * DAY_MS;

  return {
    from: new Date(thisMonday - 7 * DAY_MS).toISOString(),
    to: new Date(thisMonday - 1).toISOString(),
  };
};

/** 20.4 §4: every "Most" list in the digest, and the Top Data Rooms block, is a top three. */
export const DATA_ROOM_DIGEST_TOP = 3;

/** 20.1.1 §3: "[X] Hrs [Y] Mins", or "[X] Mins" on its own below an hour. */
/**
 * The same duration as numbers and their units, for the places that set each number apart from
 * its unit — an Activity card reads "4" large and "Hrs" small beside it.
 */
export const viewingTimeUnits = (milliseconds: number): { unit: string; value: number }[] => {
  const minutes = Math.floor(milliseconds / 60_000);
  const hours = Math.floor(minutes / 60);

  return hours
    ? [
        { unit: 'Hrs', value: hours },
        { unit: 'Mins', value: minutes % 60 },
      ]
    : [{ unit: 'Mins', value: minutes }];
};

export const formatViewingTime = (milliseconds: number): string =>
  viewingTimeUnits(milliseconds)
    .map(({ unit, value }) => `${value} ${unit}`)
    .join(' ');

/**
 * The same duration split in two, for the places that set the number and its unit in different
 * type — the strip at a data room's root reads "2" large and "Hours" small beside it.
 */
export const viewingTimeParts = (milliseconds: number): { unit: string; value: number } => {
  const minutes = Math.floor(milliseconds / 60_000);
  const hours = Math.floor(minutes / 60);

  return hours ? { unit: 'Hours', value: hours } : { unit: 'Mins', value: minutes };
};

/**
 * The same duration as the figures the Activity metric cards set: each number in its own size,
 * with its unit beside it — "10 Hrs 4 Mins", or "8 Mins" on its own below an hour.
 *
 * Beside `formatViewingTime` rather than parsed out of it: the strings are one rule, and a screen
 * that needs the parts should not be splitting a sentence back up to find them.
 */
export const viewingTimeSegments = (milliseconds: number): { unit: string; value: string }[] => {
  const minutes = Math.floor(milliseconds / 60_000);
  const hours = Math.floor(minutes / 60);

  return hours
    ? [
        { unit: 'Hrs', value: String(hours) },
        { unit: 'Mins', value: String(minutes % 60) },
      ]
    : [{ unit: 'Mins', value: String(minutes) }];
};

/** The five numbers every scope of the Activity tab reports — company, room, file or visitor. */
export interface DataRoomActivityTotals {
  avgSessionMs: number;
  downloads: number;
  uniqueVisitors: number;
  viewingTimeMs: number;
  views: number;
}

export interface DataRoomEngagementSeries {
  /** Null for the "Other" line that 20.1.1 §4 folds everything past the top five into. */
  dataRoomId: null | string;
  name: string;
}

export interface DataRoomEngagementPoint {
  date: string;
  /** Views that day, keyed by `dataRoomId` — or `other` for the folded line. */
  views: Record<string, number>;
}

/** 20.1.1 §4: at most five rooms get a line of their own. */
export const MAX_ENGAGEMENT_SERIES = 5;

export const OTHER_ENGAGEMENT_SERIES_KEY = 'other';

export interface DataRoomEngagement {
  points: DataRoomEngagementPoint[];
  series: DataRoomEngagementSeries[];
}

export interface MostViewedDataRoomCard {
  dataRoomId: string;
  downloads: number;
  name: string;
  views: number;
}

export interface MostViewedFileCard {
  dataRoomId: string;
  dataRoomName: string;
  downloads: number;
  fileId: string;
  fileName: string;
  views: number;
}

/**
 * 20.1.1 §5 identifies a visitor by the person, not by the grant: somebody invited to three rooms
 * is one Most Active Visitor, so the email is the identity and the grants are what it is summed
 * over.
 */
export interface MostActiveVisitorCard {
  email: string;
  name: string;
  organisation: string;
  viewingTimeMs: number;
  views: number;
  /** How 20.1.4 addresses this person, so the card's link opens their profile. */
  visitorKey: string;
}

/** One row of the Data Rooms list — 20.1.2 §2. */
export interface DataRoomActivityRow {
  dataRoomId: string;
  downloads: number;
  /** The most recent View or Download in the window; null when the room was quiet in it. */
  lastActivityAt: null | string;
  name: string;
  status: DataRoomStatus;
  uniqueVisitors: number;
  viewingTimeMs: number;
  views: number;
}

export interface DataRoomActivityPage {
  count: number;
  records: DataRoomActivityRow[];
  totalPages: number;
}

/** 20.1.2 §9: one of the four most-opened files of a room. */
export interface RoomPopularFile {
  downloads: number;
  fileId: string;
  folderName: string;
  name: string;
  views: number;
  visitors: number;
}

/** 20.1.2 §10: a folder with everything under it rolled up; Files is current state. */
export interface RoomFolderAnalytics {
  downloads: number;
  files: number;
  folderId: string;
  name: string;
  views: number;
  visitors: number;
}

/**
 * 20.1.2 §11: one visitor as this room saw them.
 *
 * The identity is the snapshot the journal kept and the access status is the live grant — they
 * answer different questions, so a visitor whose access was revoked keeps the history they made.
 */
export interface RoomVisitor {
  accessStatus: DataRoomAccessStatus | null;
  downloads: number;
  email: string;
  lastActiveAt: null | string;
  name: string;
  organisation: string;
  /** 20.1.4 shows a job title; nothing fills the column yet, so it reads as a dash. */
  title: null | string;
  viewingTimeMs: number;
  views: number;
  /** How the row links to the visitor's profile — see `DATA_ROOM_VISITOR_KEY_LENGTH`. */
  visitorKey: string;
}

export interface DataRoomActivityDetail {
  dataRoomId: string;
  engagement: DataRoomEngagement;
  /** Current state, never the window — 20.1.2 §7, like Active Data Rooms on Overview. */
  files: number;
  folderAnalytics: RoomFolderAnalytics[];
  folders: number;
  name: string;
  popularFiles: RoomPopularFile[];
  status: DataRoomStatus;
  totals: DataRoomActivityTotals;
  /** How many visitors the room has in the window, of which `visitors` is the first page. */
  visitorCount: number;
  visitors: RoomVisitor[];
}

/** 20.1.2 §9 and §10 both show four rows. */
export const ROOM_TOP_ROWS = 4;

/**
 * Visitor Activity is the one panel on that page with no count of its own in the story, so it
 * gets a page rather than the whole list: a popular room's visitors are unbounded, and the panel
 * sits under three that are capped at four.
 */
export const ROOM_VISITOR_ROWS = 10;

/**
 * 20.1.3 §3: which way the arrow points, by comparing the window with the one of equal length
 * before it. Not stored anywhere — it is two counts and a comparison, and it lives on the Files
 * tab alone.
 */
export enum ViewsTrend {
  DOWN = 'down',
  FLAT = 'flat',
  UP = 'up',
}

export const viewsTrend = (views: number, previousViews: number): ViewsTrend => {
  if (views > previousViews) return ViewsTrend.UP;

  return views < previousViews ? ViewsTrend.DOWN : ViewsTrend.FLAT;
};

/** One row of the Files tab — 20.1.3 §2. */
export interface FileActivityRow {
  dataRoomId: string;
  dataRoomName: string;
  downloads: number;
  fileId: string;
  folderId: string;
  folderName: string;
  /** 20.1.0 §9, and views only (§2) — the most recent View, whatever window is selected. */
  lastViewedAt: null | string;
  name: string;
  trend: ViewsTrend;
  /** Distinct visitors who viewed this file, not sessions — 20.1.3 §2. */
  uniqueViews: number;
  views: number;
}

export interface FileActivityPage {
  count: number;
  records: FileActivityRow[];
  totalPages: number;
}

export interface DataRoomActivityOverview {
  /** Current state, never the window — 20.1.1 §2. */
  activeDataRooms: number;
  engagement: DataRoomEngagement;
  /** Whether the company has any data room at all, which is a different empty state from no activity. */
  hasDataRooms: boolean;
  mostActiveVisitor: MostActiveVisitorCard | null;
  mostDownloadedFile: MostViewedFileCard | null;
  mostViewedDataRoom: MostViewedDataRoomCard | null;
  mostViewedFile: MostViewedFileCard | null;
  totals: DataRoomActivityTotals;
}

/**
 * How a visitor is addressed in a URL — 20.1.4 §5.
 *
 * Not their email. A path segment travels: into access logs, into Sentry breadcrumbs, into the
 * browser's own history and into every link an Admin pastes to a colleague, and the audience of a
 * data room is the last thing that should be scattered there. Not a row id either — the identity
 * this screen is about is the snapshot the journal kept, and the grant that id would come from may
 * already be deleted. So the key is derived from the snapshot itself: the first half of a sha256
 * over the lowercased address, computed in SQL next to the address it belongs to. It is stable
 * across sessions, recomputable from any row the journal holds, still resolvable after the grant
 * is gone, and one-way — nobody reads an address back out of a link.
 */
export const DATA_ROOM_VISITOR_KEY_LENGTH = 32;

/** 20.1.4 §3: the columns a reader may re-sort the Visitors tab by, and nothing else. */
export enum VisitorSortColumn {
  DATA_ROOM = 'data-room',
  DOWNLOADS = 'downloads',
  LAST_ACTIVE = 'last-active',
  TIME_SPENT = 'time-spent',
  VIEWS = 'views',
  VISITOR = 'visitor',
}

/**
 * One row of the Visitors tab — 20.1.4 §2.
 *
 * One visitor in one data room, not one person: 20.1.4 §1 lists the room as a column of the row,
 * so somebody invited to three rooms is three rows here. That is the opposite of the Most Active
 * Visitor card (20.1.1 §5), which sums a person across their grants — both are deliberate, and
 * this is the one the story asks for on this screen.
 */
export interface VisitorActivityRow {
  dataRoomId: string;
  dataRoomName: string;
  downloads: number;
  email: string;
  /** Their whole history in this room, never the window — 20.1.0 §9. */
  lastActiveAt: null | string;
  name: string;
  organisation: string;
  title: null | string;
  viewingTimeMs: number;
  views: number;
  visitorKey: string;
}

export interface VisitorActivityPage {
  count: number;
  /** Every organisation present in the window, for the filter — "All Organizations" is no filter. */
  organisations: string[];
  records: VisitorActivityRow[];
  totalPages: number;
}

/**
 * 20.1.4 §7's four numbers, and the one decision this screen could not avoid.
 *
 * §6 reads them as the visitor's totals across every room; §7 reads them as the selected room's.
 * One row of cards cannot be both, so they follow the room selector: "All Data Rooms" gives §6 and
 * a specific room gives §7. The alternative — fixed totals beside a selector that moves the
 * timeline under them — puts two different scopes on one screen with nothing to tell them apart.
 */
export interface VisitorActivityTotals {
  downloads: number;
  /** Outside the window, like every other Last Active in the tab — 20.1.0 §9. */
  lastActiveAt: null | string;
  viewingTimeMs: number;
  views: number;
}

/** 20.1.4 §5: who the visitor is, as the journal recorded them. */
export interface VisitorProfileIdentity {
  email: string;
  name: string;
  organisation: string;
  title: null | string;
  visitorKey: string;
}

/**
 * One grant in the room selector — 20.1.4 §6.
 *
 * Sourced from the grants rather than from the journal, so a room the visitor was invited to and
 * never opened is still selectable, and a revoked grant is still listed. A room deleted outright
 * takes its grant with it by cascade and so cannot appear — the journal keeps that history, the
 * selector cannot offer it, and that is the §11 trade-off, not an omission to fix.
 */
export interface VisitorProfileRoom {
  accessStatus: DataRoomAccessStatus;
  dataRoomId: string;
  name: string;
}

/** One View or Download on the visitor's timeline — 20.1.4 §8. */
export interface VisitorTimelineEntry {
  dataRoomId: string;
  dataRoomName: string;
  /** The file the event was logged against, so the timeline can open it; it may since be gone. */
  fileId: string;
  /** Null for a bulk download, which is asked of a folder or a room rather than of a file. */
  fileName: null | string;
  id: string;
  occurredAt: string;
  /** Null on a View; a download says what was asked for — 20.1.0 §7. */
  scope: DataRoomDownloadScope | null;
  type: DataRoomActivityEventType;
}

/**
 * The timeline, grouped by calendar day — 20.1.4 §8.
 *
 * Days are cut in UTC, the same way `dataRoomActivityRange` cuts the window and the engagement
 * chart cuts its buckets. The viewer's own midnight is never sent to the server, and two screens
 * disagreeing about which day a Tuesday-evening view fell on is worse than one named timezone.
 */
export interface VisitorTimelineDay {
  day: string;
  entries: VisitorTimelineEntry[];
}

export interface VisitorProfile {
  /** Present only while a specific room is selected — across rooms there is no one status. */
  accessStatus: DataRoomAccessStatus | null;
  identity: VisitorProfileIdentity;
  rooms: VisitorProfileRoom[];
  timeline: VisitorTimelineDay[];
  /** How many entries the timeline has in the window, of which `timeline` is the first page. */
  timelineCount: number;
  totals: VisitorActivityTotals;
}

/** 20.1.4 §8: one page of the timeline, and what "Show more" asks for next. */
export const VISITOR_TIMELINE_ENTRIES = 25;
